import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X, Plus, AlertCircle, CheckCircle, ChevronDown, ChevronUp, Tag, Search, Layers } from "lucide-react";
import api from "../api/axiosInstance";
import { countOwnProducts } from "../utils/productCount";
import fetchWithJWT from "../api/fetchWithJWT";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { usePlan } from "../context/PlanContext";
import subscriptionService from "../services/subscriptionService";
import paymentManagementService, { PAYMENT_TYPE } from "../services/paymentManagementService";
import ProductTypeSelector from "./ProductTypeSelector";
import EbookFileUpload from "./EbookFileUpload";
import DigitalProductFields from "./DigitalProductFields";
import AIDescriptionGenerator from "./AIDescriptionGenerator";
import S from "./Addgiftstyles";
import ProductShippingDetails, { defaultWeightType, rowsFromShipping, weightRows, weightsToSave, weightProblem } from "./ProductShippingDetails";
import useShippingStatus from "../hooks/useShippingStatus";
import { buildCategoryOptions, buildSubcategoryOptions, normaliseName } from "../utils/categoryOptions";

// ─── Toggle switch ─────────────────────────────────────────────────────────────
// FIX: e.stopPropagation() prevents parent div onClick from double-firing
function Toggle({ on, onChange }) {
  return (
    <button
      type="button"
      style={S.toggleTrack(on)}
      onClick={(e) => { e.stopPropagation(); onChange(!on); }}
    >
      <div style={S.toggleThumb(on)} />
    </button>
  );
}

// ─── Collapsible section card ──────────────────────────────────────────────────
function Section({ icon, iconColor = "green", title, subtitle, toggle, enabled, onToggle, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const showBody = toggle ? enabled : open;

  return (
    <div style={S.card}>
      <div
        style={{ ...S.cardHeader, cursor: toggle ? "default" : "pointer" }}
        onClick={() => { if (!toggle) setOpen((p) => !p); }}
      >
        <div style={S.cardHeaderLeft}>
          <div style={S.cardIconWrap(iconColor)}>{icon}</div>
          <div>
            <p style={S.cardTitle}>{title}</p>
            {subtitle && <p style={S.cardSubtitle}>{subtitle}</p>}
          </div>
        </div>
        {toggle
          ? <Toggle on={enabled} onChange={onToggle} />
          : <span style={{ color: "#8A7E6A" }}>{open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</span>
        }
      </div>
      {showBody && <div style={S.cardBody}>{children}</div>}
    </div>
  );
}

// ─── Field wrapper ─────────────────────────────────────────────────────────────
function Field({ label, required, hint, error, children }) {
  return (
    <div style={S.fieldWrap}>
      {label && (
        <label style={S.label}>
          {label}{required && <span style={S.required}>*</span>}
        </label>
      )}
      {children}
      {hint && !error && <p style={S.hint}>{hint}</p>}
      {error && <div style={S.errorText}><AlertCircle size={11} />{error}</div>}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

function AddGift() {
  const { t } = useTranslation();
  const { canAddProduct, planInfo } = usePlan();
  const [searchParams] = useSearchParams();
  const giftId = searchParams.get("id");
  const navigate = useNavigate();

  // Basic
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [productType, setProductType] = useState("physical");
  const [hsnCode, setHsnCode] = useState("");
  const [isGstApplicable, setIsGstApplicable] = useState(false);
  const [hsnCodesList, setHsnCodesList] = useState([]);

  // Categories
  // { marketplace: Super Admin category names, store: this store's own }
  const [categoryData, setCategoryData] = useState({ marketplace: [], store: [] });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [subCategoryData, setSubCategoryData] = useState({});
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newSubCategory, setNewSubCategory] = useState("");
  const [addCatError, setAddCatError] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);
  const [showAddSubcatFor, setShowAddSubcatFor] = useState(null);
  const [newSubcategoryName, setNewSubcategoryName] = useState("");
  const [addSubcatError, setAddSubcatError] = useState("");
  const [isCreatingSubcategory, setIsCreatingSubcategory] = useState(false);

  // Media
  const [images, setImages] = useState([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploadedVideoUrls, setUploadedVideoUrls] = useState([]);
  const [videoUploadProgress, setVideoUploadProgress] = useState({});

  // Digital
  const [digitalFileData, setDigitalFileData] = useState(null);
  const [digitalSettings, setDigitalSettings] = useState({ download_limit: 5, has_expiry: false, access_expiry_days: null });

  // Identity (optional)
  const [identityEnabled, setIdentityEnabled] = useState(false);
  const [sku, setSku] = useState("");
  const [barcode, setBarcode] = useState("");
  const [brand, setBrand] = useState("");
  const [manufacturer, setManufacturer] = useState("");

  // Variants (optional)
  const [variantsEnabled, setVariantsEnabled] = useState(false);
  const [variants, setVariants] = useState([]);
  const [newVariantType, setNewVariantType] = useState("");
  const [newVariantValues, setNewVariantValues] = useState("");
  const [isCustomVariantType, setIsCustomVariantType] = useState(false);

  // Variant Images (Color-to-Image mapping)
  const [variantImages, setVariantImages] = useState([]);
  const [uploadingVariantImage, setUploadingVariantImage] = useState(null);

  // Shipping (ProductShippingDetails.js): packed weight and box per product
  // or per variant, what customers pay, COD. The store's shipping setup says
  // whether weights are required (a courier store).
  const shipping = useShippingStatus();
  const [weightType, setWeightType] = useState("");
  const [perCombination, setPerCombination] = useState(false);
  const [weightRowsState, setWeightRowsState] = useState({});
  const [missingWeightKey, setMissingWeightKey] = useState(null);
  const [freeShipping, setFreeShipping] = useState(false);
  const effectiveWeightType = (variantsEnabled ? variants : []).some((v) => v && v.type === weightType && (v.values || []).length)
    ? weightType
    : defaultWeightType(variantsEnabled ? variants : []);
  const [shippingChargeType, setShippingChargeType] = useState("fixed");
  const [shippingFixedCharge, setShippingFixedCharge] = useState("");
  const [shippingRatePerKg, setShippingRatePerKg] = useState("");
  // On for a new product, like the API's default; the seller can turn it off
  const [codAvailable, setCodAvailable] = useState(true);

  // SEO (optional)
  const [seoEnabled, setSeoEnabled] = useState(false);
  const [seoMetaTitle, setSeoMetaTitle] = useState("");
  const [isMetaTitleManuallyEdited, setIsMetaTitleManuallyEdited] = useState(false);
  const [seoMetaDescription, setSeoMetaDescription] = useState("");
  const [seoTags, setSeoTags] = useState([]);
  const [seoTagInput, setSeoTagInput] = useState("");
  const [seoSlug, setSeoSlug] = useState("");
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
  const [seoGenerating, setSeoGenerating] = useState(false);
  const [storeDomain, setStoreDomain] = useState("[your-store].mytamilkart.com");

  // UI state
  const [titleError, setTitleError] = useState("");
  const [subCategoryError, setSubCategoryError] = useState("");
  const [mediaError, setMediaError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const [checkingPlanAccess, setCheckingPlanAccess] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showHSNModal, setShowHSNModal] = useState(false);
  const [newHSN, setNewHSN] = useState({ hsn_code: "", gst_percentage: "", description: "", category: "" });
  const [hsnErrors, setHsnErrors] = useState({});
  const [productCount, setProductCount] = useState(0);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);

  // FIX: Auto-populate meta title from product title ONLY when SEO is enabled
  // Split into two effects to prevent the "cannot clear meta title" bug

  // Effect 1: Reset flags and auto-populate when SEO is enabled
  useEffect(() => {
    if (seoEnabled) {
      setIsMetaTitleManuallyEdited(false);
      setIsSlugManuallyEdited(false);
      if (title && !seoMetaTitle) {
        setSeoMetaTitle(title);
      }
    }
  }, [seoEnabled]);

  // Effect 2: Update meta title and slug from title
  useEffect(() => {
    if (seoEnabled && title) {
      // Auto-populate meta title if it hasn't been manually edited
      if (!isMetaTitleManuallyEdited) {
        setSeoMetaTitle(title);
      }
      
      // Auto-populate slug from title if it hasn't been manually edited
      if (!isSlugManuallyEdited) {
        setSeoSlug(sanitizeSlug(title));
      }
    }
  }, [title, seoEnabled, isSlugManuallyEdited, isMetaTitleManuallyEdited]);
  
  const previewSlug = useMemo(() => sanitizeSlug(title), [title]);

  // Plan check
  useEffect(() => {
    const check = async () => {
      const bid = localStorage.getItem("business_id");
      if (!bid) { toast.error("Session expired."); setCheckingPlanAccess(false); return; }
      try {
        const [pr, sr] = await Promise.all([
          fetchWithJWT(`/business/plan/info?business_user_id=${bid}`),
          fetchWithJWT(`/subscription/info?business_user_id=${bid}`),
        ]);
        const pd = await pr.json();
        const sd = await sr.json();
        const hasActivePlan = pd.status === "success" && pd.result?.plan_name &&
          pd.result.plan_name.toLowerCase() !== "unknown plan" &&
          !["none", "created", "cancelled"].includes(pd.result.plan_status) &&
          !pd.result.is_expired;
        const hasSub = sd.status === "success" && sd.result?.has_subscription &&
          (sd.result.paid_count > 0 || sd.result.first_payment_discount_amount > 0) &&
          sd.result.status !== "cancelled";
        if (!hasActivePlan && !hasSub) {
          const res = await api.get("/gift/list", { params: { business_id: bid } });
          const count = res.data.status === "success" ? countOwnProducts(res.data.result) : 0;
          if (count >= 1) {
            toast.warning("Please purchase a plan to add more products.", { position: "top-center", autoClose: 3000, toastId: "plan-required" });
            navigate("/purchase-plan", { replace: true });
            return;
          }
        }
      } catch (e) { console.error(e); }
      setCheckingPlanAccess(false);
    };
    check();
  }, [navigate]);

  useEffect(() => {
    if (!checkingPlanAccess) {
      loadCategories(); loadHSNCodes(); loadProductCount();
      const bid = localStorage.getItem("business_id");
      if (bid) {
        loadSubscription(bid);
        loadPaymentInfo(bid);

        // Fetch store info for the exact subdomain
        const fetchStore = async () => {
          try {
            const r = await fetchWithJWT(`/business/store/info?business_id=${bid}`);
            const data = await r.json();
            if (data.status === "success" && data.result?.store_subdomain) {
              setStoreDomain(`${data.result.store_subdomain}.mytamilkart.com`);
            } else {
              // Security Fix: Use new URL() for safer sanitization
              const storeUrl = localStorage.getItem("store_url");
              if (storeUrl) {
                try {
                  const url = new URL(storeUrl);
                  setStoreDomain(url.hostname);
                } catch (e) {
                  setStoreDomain(storeUrl.replace(/^https?:\/\//, ""));
                }
              }
            }
          } catch (e) {
            console.error("Error fetching store info:", e.message);
          }
        };
        fetchStore();
      }
    }
  }, [checkingPlanAccess]);

  useEffect(() => { if (giftId) loadGiftData(giftId); }, [giftId]);

  // ── Data loaders ──────────────────────────────────────────────────────────────
  const loadCategories = () => {
    const bid = localStorage.getItem("business_id");
    if (!bid) return;
    // The marketplace (Super Admin) categories plus the store's own.
    api.get("/category/list", { params: { business_id: bid, include_platform: "true" } }).then((r) => {
      if (r.data.status === "success") setCategoryData(buildCategoryOptions(r.data.result));
    }).catch(console.error);
  };

  const loadProductCount = async () => {
    const bid = localStorage.getItem("business_id");
    if (!bid) return;
    try {
      const r = await api.get("/gift/list", { params: { business_id: bid } });
      if (r.data.status === "success") setProductCount(countOwnProducts(r.data.result));
    } catch (error) {
      console.error("Error loading product count:", error);
      toast.error("Failed to load product count");
    }
  };

  const loadSubscription = async (bid) => {
    try { setSubscriptionInfo(await subscriptionService.getSubscriptionInfo(bid)); } catch (e) { console.error(e); }
  };

  const loadPaymentInfo = async (bid) => {
    try { setPaymentInfo(await paymentManagementService.getPaymentInfo(bid)); } catch (e) { console.error(e); }
  };

  const loadHSNCodes = async () => {
    const bid = localStorage.getItem("business_id");
    if (!bid) return;
    const r = await api.get("/hsn/list", { params: { business_id: bid } });
    if (r.data.status === "success") setHsnCodesList(r.data.result.list || []);
  };

  const loadGiftData = (id) => {
    api.get("/gift/list", { params: { gift_id: id, admin: true } }).then((r) => {
      if (r.data.status !== "success") return;
      const d = r.data.result;
      setTitle(d.name); setPrice(d.price); setQuantity(d.quantity);
      setHsnCode(d.hsn_code || ""); setIsGstApplicable(d.is_gst_applicable !== false);
      setDescription(d.description || "");
      if (d.images?.length) setUploadedImageUrls(d.images);
      else if (d.url) setUploadedImageUrls([d.url]);
      if (d.videos?.length) setUploadedVideoUrls(d.videos);

      // Identity
      if (d.sku || d.barcode || d.brand || d.manufacturer) {
        setIdentityEnabled(true);
        setSku(d.sku || ""); setBarcode(d.barcode || "");
        setBrand(d.brand || ""); setManufacturer(d.manufacturer || "");
      }

      // Variants
      if (d.variants?.length) {
        setVariantsEnabled(true);
        setVariants(d.variants.filter(Boolean).map((v) => ({ ...v, values: Array.isArray(v.values) ? v.values : [] })));
      }

      // Variant Images (Color-to-Image mapping)
      if (d.variant_images?.length) { setVariantImages(d.variant_images); }

      // Shipping
      if (d.shipping?.weight || d.shipping?.charge_type || d.shipping?.variant_weights?.length) {
        setWeightRowsState(rowsFromShipping(d.shipping));
        setWeightType(d.shipping.weight_variant_type || "");
        setPerCombination(!!d.shipping.weight_per_combination);
        setFreeShipping(d.shipping.charge_type === "free");
        setShippingChargeType(d.shipping.charge_type === "dynamic" ? "dynamic" : "fixed");
        setShippingFixedCharge(d.shipping.fixed_charge ?? "");
        setShippingRatePerKg(d.shipping.rate_per_kg ?? "");
        setCodAvailable(d.shipping.cod_available !== false);
      }

      // FIX: Load SEO — meta_title, meta_description, tags, slug
      if (d.seo?.meta_title || d.seo?.meta_description || d.seo?.tags?.length || d.seo?.slug) {
        setSeoEnabled(true);
        if (d.seo) {
          if (d.seo.meta_title) {
            setSeoMetaTitle(d.seo.meta_title);
            setIsMetaTitleManuallyEdited(true); // treat loaded values as "manually set"
          }
          if (d.seo.slug) {
            setSeoSlug(d.seo.slug);
            setIsSlugManuallyEdited(true);
          }
          setSeoTags(d.seo.tags || []);
          setSeoMetaDescription(d.seo.meta_description || "");
        }
      }

      // Digital
      if (d.product_type === "digital" || d.is_digital) {
        setProductType("digital");
        if (d.file_key) setDigitalFileData({ file_key: d.file_key, file_name: d.file_name, file_format: d.file_format, file_size: d.file_size });
        setDigitalSettings({ download_limit: d.download_limit || 5, has_expiry: d.access_expiry_days !== null, access_expiry_days: d.access_expiry_days || 30 });
      }

      // Categories
      if (Array.isArray(d.category_id)) {
        const unique = d.category_id.reduce((a, c) => {
          const k = `${c._id}_${c.category}_${c.subcategory}`;
          if (!a.find((i) => `${i._id}_${i.category}_${i.subcategory}` === k)) a.push(c);
          return a;
        }, []);
        const cats = [...new Set(unique.map((c) => c.category || c.parentCategory))];
        setSelectedCategories(cats);
        setSelectedSubCategories(unique.map((c) => ({ id: c._id || c.id, subcategory: c.subcategory || c.name, parentCategory: c.category || c.parentCategory, _id: c._id })));
        cats.forEach(loadSubCategories);
      }
    }).catch(console.error);
  };

  // ── Category helpers ──────────────────────────────────────────────────────────
  const loadSubCategories = (cat) => {
    const bid = localStorage.getItem("business_id");
    if (!bid) return;
    // The backend explicitly does decodeURIComponent(category) on top of
    // Express's own query-string decoding (see event.js), so this extra
    // encode layer here is intentional, not double-encoding - reverting it
    // breaks category names containing a literal % (decodeURIComponent then
    // throws URIError server-side).
    api.get("/category/list", { params: { business_id: bid, include_platform: "true", category: encodeURIComponent(cat.trim()) } }).then((r) => {
      const list = Array.isArray(r.data.result) ? r.data.result : [];
      setSubCategoryData((p) => ({ ...p, [cat]: buildSubcategoryOptions(list, cat) }));
    }).catch(console.error);
  };

  const isCategorySelected = (cat) => selectedCategories.some((c) => normaliseName(c) === normaliseName(cat));

  const handleCategorySelect = (cat) => {
    if (!isCategorySelected(cat)) { setSelectedCategories((p) => [...p, cat]); loadSubCategories(cat); }
  };

  const handleRemoveCategory = (cat) => {
    setSelectedCategories(selectedCategories.filter((c) => c !== cat));
    setSelectedSubCategories(selectedSubCategories.filter((s) => s.parentCategory !== cat));
  };

  const handleSubCategorySelect = (sub) => {
    const exists = selectedSubCategories.find((s) => (s.id === sub.id || s._id === sub.id) && s.parentCategory === sub.parentCategory);
    if (!exists) setSelectedSubCategories([...selectedSubCategories, sub]);
  };

  const handleRemoveSubCategory = (id) => setSelectedSubCategories(selectedSubCategories.filter((s) => s.id !== id && s._id !== id));

  // Helper function to parse category/subcategory error responses
  const parseCategoryError = (e, context = 'category') => {
    const errorData = e.response?.data?.error;

    // Check for category limit exceeded error
    if (errorData?.field_name === "category_limit_exceeded") {
      return errorData?.message || "Maximum 10 categories allowed per business. You can add unlimited subcategories to existing categories.";
    }

    // Return generic error message
    const defaultMsg = context === 'subcategory'
      ? "Failed to create subcategory."
      : "Failed to create category.";
    return errorData?.message || e.response?.data?.message || defaultMsg;
  };

  const handleCreateCategory = async () => {
    if (!newCategory || !newSubCategory) { setAddCatError("Both fields required."); return; }
    setIsCreatingCategory(true);
    try {
      const bid = localStorage.getItem("business_id");
      const r = await api.post("/category/create", { category: newCategory, subcategory: newSubCategory, category_id: "", type: "gift", column_header: `${newCategory} Collection`, column_priority: 0, is_featured: false, is_trending: false, display_order: 0, business_id: bid });
      if (r.data.status === "success") {
        // The API may save the category under an existing spelling
        // ("fashion" -> "Fashion").
        const saved = r.data.result?.data || {};
        const categoryName = saved.category || newCategory.trim();
        loadCategories();
        handleCategorySelect(categoryName);
        // Already selected (same name, any case): handleCategorySelect skips
        // it, so refresh its subcategory list here.
        loadSubCategories(categoryName);
        if (saved._id) handleSubCategorySelect({ id: saved._id, subcategory: saved.subcategory || newSubCategory.trim(), parentCategory: categoryName });
        setShowAddCategoryForm(false); setNewCategory(""); setNewSubCategory(""); setAddCatError("");
      } else { setAddCatError("Failed to create."); }
    } catch (e) {
      setAddCatError(parseCategoryError(e, 'category'));
    }
    setIsCreatingCategory(false);
  };

  const handleCreateSubcategory = async (parentCat) => {
    if (!newSubcategoryName) { setAddSubcatError("Name required."); return; }
    setIsCreatingSubcategory(true);
    try {
      const bid = localStorage.getItem("business_id");
      const r = await api.post("/category/create", { category: parentCat, subcategory: newSubcategoryName, category_id: "", type: "gift", column_header: `${parentCat} Collection`, column_priority: 0, is_featured: false, is_trending: false, display_order: 0, business_id: bid });
      if (r.data.status === "success") {
        const saved = r.data.result?.data || {};
        loadSubCategories(parentCat);
        handleSubCategorySelect({ id: saved._id, subcategory: saved.subcategory || newSubcategoryName.trim(), parentCategory: parentCat });
        setShowAddSubcatFor(null); setNewSubcategoryName(""); setAddSubcatError("");
        toast.success("Subcategory added!");
      } else { setAddSubcatError("Failed."); }
    } catch (e) {
      setAddSubcatError(parseCategoryError(e, 'subcategory'));
    }
    setIsCreatingSubcategory(false);
  };

  // ── Media helpers ─────────────────────────────────────────────────────────────
  const dataURItoBlob = (dataURI, name) => {
    const bs = atob(dataURI.split(",")[1]), mime = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(bs.length), ia = new Uint8Array(ab);
    for (let i = 0; i < bs.length; i++) ia[i] = bs.charCodeAt(i);
    return new File([ab], name, { type: mime });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const total = images.length + uploadedImageUrls.length + videos.length + uploadedVideoUrls.length;
    const remaining = 5 - total;
    if (files.length > remaining) { toast.error(`Only ${remaining} more media item(s) allowed.`); return; }
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        // FIX: use window.Image() to avoid conflict with lucide-react Image import
        const img = new window.Image();
        img.src = ev.target.result;
        img.onload = () => {
          if (img.width > 2000 || img.height > 2000) {
            const canvas = document.createElement("canvas");
            const ratio = Math.min(1920 / img.width, 1080 / img.height);
            canvas.width = img.width * ratio; canvas.height = img.height * ratio;
            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setImages((p) => [...p, dataURItoBlob(canvas.toDataURL("image/jpeg", 0.92), file.name)]);
          } else {
            setImages((p) => [...p, file]);
          }
        };
      };
      reader.readAsDataURL(file);
    });
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    const total = images.length + uploadedImageUrls.length + videos.length + uploadedVideoUrls.length;
    const remaining = 5 - total;
    if (files.length > remaining) { toast.error(`Only ${remaining} more media item(s) allowed.`); return; }
    const valid = files.filter((f) => {
      if (!["video/mp4", "video/mov", "video/webm", "video/quicktime"].includes(f.type)) { toast.error(`${f.name}: invalid format`); return false; }
      if (f.size > 100 * 1024 * 1024) { toast.error(`${f.name}: exceeds 100MB`); return false; }
      return true;
    });
    if (valid.length) { setVideos((p) => [...p, ...valid]); toast.success(`${valid.length} video(s) added`); }
  };

  // ── Variant helpers ───────────────────────────────────────────────────────────
  const handleAddVariant = () => {
    if (!newVariantType.trim() || !newVariantValues.trim()) { toast.error("Enter type and at least one value."); return; }
    const vals = newVariantValues.split(",").map((v) => v.trim()).filter(Boolean);
    if (variants.find((v) => v.type.toLowerCase() === newVariantType.trim().toLowerCase())) { toast.error("Variant type already added."); return; }
    setVariants([...variants, { type: newVariantType.trim(), values: vals }]);
    setNewVariantType(""); setNewVariantValues(""); setIsCustomVariantType(false);
  };

  // ── Variant Image helpers (Color-to-Image mapping) ────────────────────────────
  const handleVariantImageUpload = async (color, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingVariantImage(color);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_type', 'image');

      const response = await api.post('/file/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.status === 'success') {
        // API returns result as array: result[0].file_url
        const imageUrl = response.data.result?.[0]?.file_url || response.data.result?.files?.[0]?.file_url;

        if (!imageUrl) {
          console.error('Failed to extract URL. Response:', response.data);
          toast.error('Failed to get image URL from upload response');
          return;
        }

        // Update or add variant image
        setVariantImages(prev => {
          const existing = prev.find(vi => vi.color === color);
          if (existing) {
            return prev.map(vi => vi.color === color ? { color, image_url: imageUrl } : vi);
          } else {
            return [...prev, { color, image_url: imageUrl }];
          }
        });

        toast.success(`Image uploaded for ${color}`);
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Variant image upload error:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setUploadingVariantImage(null);
    }
  };

  const handleRemoveVariantImage = (color) => {
    setVariantImages(prev => prev.filter(vi => vi.color !== color));
    toast.success(`Image removed for ${color}`);
  };

  // ── SEO tag helpers ───────────────────────────────────────────────────────────
  // FIX 6: Shared sanitizeSlug function
  function sanitizeSlug(value) {
    return (value || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')   // remove special chars
      .replace(/\s+/g, '-')            // spaces to hyphens
      .replace(/-+/g, '-')             // collapse multiple hyphens
      .replace(/^-+|-+$/g, '');        // trim leading/trailing hyphens
  }

  const handleAddTag = () => {
    const tag = seoTagInput.trim().toLowerCase();
    if (tag && !seoTags.includes(tag)) { setSeoTags([...seoTags, tag]); setSeoTagInput(""); }
  };

  const handleGenerateSEO = async () => {
    if (!title.trim()) {
      toast.error("Please enter a product name first");
      return;
    }
    setSeoGenerating(true);
    try {
      const res = await api.post("/ai/generate-seo", {
        product_name: title,
        description: description,
        categories: selectedCategories,
      });

      if (res.data.status === "success") {
        // Only overwrite if AI returned a value — never clear existing content
        if (res.data.meta_description) {
          setSeoMetaDescription(res.data.meta_description);
        }
        if (res.data.tags && res.data.tags.length > 0) {
          setSeoTags(res.data.tags);
        }
        toast.success("✨ SEO content generated successfully!");
      } else {
        toast.error(res.data.message || "Generation failed. Please try again.");
      }
    } catch (e) {
      console.error("SEO generation error:", e.message);
      toast.error("Generation failed. Please try again.");
    } finally {
      setSeoGenerating(false);
    }
  };

  // ── Upload helpers ────────────────────────────────────────────────────────────
  const uploadImages = async () => {
    return Promise.all(images.map(async (img) => {
      const fd = new FormData(); fd.append("file", img); fd.append("upload_type", "image");
      const r = await api.post("/file/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.status !== "success") throw new Error("Image upload failed");
      return r.data.result[0]?.file_url || r.data.result?.files?.[0]?.file_url;
    }));
  };

  const uploadVideos = async () => {
    const urls = await Promise.all(videos.map(async (vid, idx) => {
      const fd = new FormData(); fd.append("file", vid); fd.append("upload_type", "video");
      const r = await api.post("/file/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setVideoUploadProgress((p) => ({ ...p, [idx]: Math.round((e.loaded * 100) / e.total) })),
      });
      if (r.data.status !== "success") throw new Error("Video upload failed");
      return r.data.result[0]?.file_url || r.data.result?.files?.[0]?.file_url;
    }));
    setVideoUploadProgress({});
    return urls;
  };

  const checkPaymentAndNavigate = async () => {
    try {
      const bid = localStorage.getItem("business_id");
      const r = await api.get("/business/store/info", { params: { business_id: bid } });
      if (r.data.status === "success") {
        const { seller_account_id, product_config_id } = r.data.result;
        if (!seller_account_id || !product_config_id) { setShowPaymentModal(true); return; }
      }
    } catch (_) { }
    navigate("/gift");
  };

  const checkProductLimit = () => {
    if (paymentInfo?.paymentType === PAYMENT_TYPE.AUTO_RENEW && subscriptionInfo)
      return subscriptionService.canAddProduct(subscriptionInfo, productCount);
    if (planInfo) return canAddProduct(productCount);
    return { allowed: false, reason: "No active plan or subscription." };
  };

  // ── HSN ───────────────────────────────────────────────────────────────────────
  const handleAddHSNCode = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!newHSN.hsn_code.trim()) errs.hsn_code = "Required";
    if (!newHSN.gst_percentage || newHSN.gst_percentage < 0) errs.gst_percentage = "Required";
    if (Object.keys(errs).length) { setHsnErrors(errs); return; }
    try {
      const bid = localStorage.getItem("business_id");
      const r = await api.post("/hsn/create", { ...newHSN, business_id: bid, gst_percentage: parseFloat(newHSN.gst_percentage) });
      if (r.data.status === "success") {
        toast.success("HSN Code added!"); setShowHSNModal(false); loadHSNCodes();
        setNewHSN({ hsn_code: "", gst_percentage: "", description: "", category: "" }); setHsnErrors({});
      }
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsFormProcessing(true);
    setErrorMessage(""); setTitleError(""); setSubCategoryError(""); setMediaError("");

    if (!giftId) {
      const lc = checkProductLimit();
      if (!lc.allowed) {
        toast.error(lc.reason, { autoClose: 5000, onClick: () => navigate(paymentInfo?.paymentType === PAYMENT_TYPE.AUTO_RENEW ? "/subscription" : "/purchase-plan") });
        setIsFormProcessing(false); return;
      }
    }

    let valid = true;
    if (!title.trim()) { setTitleError("Product name is required."); valid = false; }
    if (selectedSubCategories.length === 0) { setSubCategoryError("Please select at least one category."); valid = false; }
    if (!price || String(price).trim() === "") { toast.error("Please enter the price."); valid = false; }

    const totalMedia = images.length + uploadedImageUrls.length + videos.length + uploadedVideoUrls.length;
    if (productType === "digital") {
      if (!digitalFileData) { toast.error("Please upload an e-book file."); valid = false; }
      if (images.length + uploadedImageUrls.length === 0) { setMediaError("A cover image is required for digital products."); valid = false; }
    } else {
      if (totalMedia === 0) { setMediaError("Please add at least one photo or video."); valid = false; }
    }

    // A courier books shipments by weight: a courier store's physical product needs one (each variant's)
    setMissingWeightKey(null);
    if (productType !== "digital" && shipping.isCourier) {
      const missing = weightProblem(weightRows(variantsEnabled ? variants : [], effectiveWeightType, perCombination), weightRowsState);
      if (missing) {
        setMissingWeightKey(missing.key);
        toast.error(`Add a weight for ${missing.key ? `the ${missing.label}` : "this product"} before saving.`);
        valid = false;
      }
    }

    if (!valid) { setIsFormProcessing(false); return; }

    try {
      let allImageUrls = [...uploadedImageUrls];
      let allVideoUrls = [...uploadedVideoUrls];
      if (images.length) { const u = await uploadImages(); allImageUrls = [...allImageUrls, ...u]; }
      if (productType === "physical" && videos.length) {
        toast.info("Uploading videos...");
        allVideoUrls = [...allVideoUrls, ...(await uploadVideos())];
      }

      const bid = localStorage.getItem("business_id");
      const payload = {
        name: title, description, price,
        quantity: productType === "digital" ? 999999 : quantity,
        hsn_code: hsnCode, is_gst_applicable: isGstApplicable,
        gift_id: giftId || "",
        categories: selectedCategories,
        category_id: [...new Set(selectedSubCategories.flatMap((s) => [s._id, s.id]).filter(Boolean).filter((x) => x.trim()))].join(","),
        subcategories: selectedSubCategories.map((s) => ({ id: s.id, parentCategory: s.parentCategory })),
        product_type: productType,
        is_digital: productType === "digital",
        digital_type: productType === "digital" ? "ebook" : null,
        file_key: productType === "digital" && digitalFileData ? digitalFileData.file_key : "",
        file_name: productType === "digital" && digitalFileData ? digitalFileData.file_name : "",
        file_format: productType === "digital" && digitalFileData ? digitalFileData.file_format : "",
        file_size: productType === "digital" && digitalFileData ? digitalFileData.file_size : 0,
        download_limit: productType === "digital" ? digitalSettings.download_limit : 5,
        access_expiry_days: productType === "digital" && digitalSettings.has_expiry ? digitalSettings.access_expiry_days : null,
        is_shipping_required: productType === "physical",
        allow_cod: productType === "physical",
        require_prepaid: productType === "digital",
        url: allImageUrls[0] || "",
        images: allImageUrls.join(","),
        videos: productType === "physical" && allVideoUrls.length ? allVideoUrls.join(",") : "",
        business_id: bid,

        // Identity — only send if section enabled
        ...(identityEnabled && {
          sku: sku.trim() || undefined,
          barcode: barcode.trim() || undefined,
          brand: brand.trim() || undefined,
          manufacturer: manufacturer.trim() || undefined,
        }),

        // Variants
        variants: variantsEnabled ? variants : [],

        // Variant Images (Color-to-Image mapping)
        variant_images: variantsEnabled && variantImages.length > 0 ? variantImages : [],

        // Shipping
        shipping: productType !== "digital" ? {
          ...weightsToSave(weightRows(variantsEnabled ? variants : [], effectiveWeightType, perCombination), weightRowsState, effectiveWeightType, perCombination),
          charge_type: freeShipping ? "free" : shippingChargeType,
          fixed_charge: !freeShipping && shippingChargeType === "fixed" ? Number(shippingFixedCharge) || 0 : 0,
          rate_per_kg: !freeShipping && shippingChargeType === "dynamic" ? Number(shippingRatePerKg) || 0 : 0,
          // The seller's own choice for this product, kept as it is: the store-wide
          // COD switch is applied at checkout (and turning it back on restores these)
          cod_available: codAvailable,
        } : {},

        // SEO
        seo: seoEnabled ? {
          meta_title: seoMetaTitle.trim(),
          meta_description: seoMetaDescription.trim(),
          tags: seoTags,
          slug: seoSlug.trim(),
        } : {
          meta_title: "",
          meta_description: "",
          tags: [],
          slug: "",
        },
      };

      const r = await api.post(giftId ? "/gift/update" : "/gift/create", payload);
      if (r.data.status === "success") {
        toast.success(giftId ? t("product_updated_successfully") : t("product_added_successfully"));
        setTimeout(() => checkPaymentAndNavigate(), 1800);
      } else {
        setErrorMessage("Failed to save product. Please try again.");
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Something went wrong.";
      setErrorMessage(msg); toast.error(msg);
    } finally {
      setIsFormProcessing(false);
    }
  };

  // ── Loading screen ────────────────────────────────────────────────────────────
  if (checkingPlanAccess) {
    return (
      <div className="wrapper">
        <header><NavigationBar /></header>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", flexDirection: "column", gap: "14px" }}>
          <div className="spinner-border text-success" role="status" style={{ width: "2.5rem", height: "2.5rem" }}><span className="sr-only">Loading...</span></div>
          <p style={{ color: "#666", fontSize: "14px" }}>Checking access...</p>
        </div>
        <Footer />
      </div>
    );
  }

  const totalMedia = images.length + uploadedImageUrls.length + videos.length + uploadedVideoUrls.length;

  // ── RENDER ────────────────────────────────────────────────────────────────────
  return (
    <div className="wrapper" style={S.page}>
      <header><NavigationBar /></header>

      <div className="content-wrapper" style={{ paddingBottom: "40px" }}>
        {/* Page header */}
        <div className="content-header">
          <div className="container-fluid">
            <div style={{ display: "flex", alignItems: "center", paddingTop: "10px", paddingBottom: "4px" }}>
              <button type="button" style={S.backBtn} onClick={() => navigate("/gift")}>
                <FaArrowLeft size={13} /> Back
              </button>
              <h1 style={{ fontSize: "20px", fontWeight: "700", color: "#241E14", margin: 0 }}>
                {giftId ? "Edit Product" : "Add New Product"}
              </h1>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div style={S.infoBox}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: "1px" }} />
              <span>Products you add here appear in your own store only. To be featured on the TamilKart main page, a platform admin must separately select this product from Web Store Settings.</span>
            </div>
            <form onSubmit={handleSubmit}>

              {/* ═══ 1: Product Type ════════════════════════════════════════ */}
              <Section
                icon={<span style={{ fontSize: "16px" }}>📦</span>}
                title="What are you selling?"
                subtitle="Physical item or a digital product (e-book, course, etc.)"
                defaultOpen={true}
              >
                <ProductTypeSelector value={productType} onChange={(type) => {
                  setProductType(type);
                  if (type === "digital") { setQuantity(999999); setDigitalSettings({ download_limit: 5, has_expiry: false, access_expiry_days: null }); }
                  else { setQuantity(""); setDigitalFileData(null); }
                }} disabled={!!giftId} />
              </Section>

              {/* ═══ 2: Product Details ═════════════════════════════════════ */}
              <Section
                icon={<span style={{ fontSize: "16px" }}>📝</span>}
                title="Product Details"
                subtitle="Name, description, price and quantity"
                defaultOpen={true}
              >
                <Field label="Product Name" required error={titleError}>
                  <input
                    style={S.input} type="text" value={title}
                    onChange={(e) => { setTitle(e.target.value); setTitleError(""); }}
                    placeholder="e.g. Custom Birthday Mug"
                  />
                </Field>



                <div style={S.row2}>
                  <Field label="Price (₹)" required>
                    <input style={S.input} type="number" value={price} onChange={(e) => setPrice(e.target.value)} min="2" placeholder="0.00" />
                  </Field>
                  <Field label="Quantity" hint={productType === "digital" ? "Unlimited for digital products" : undefined}>
                    {productType === "digital"
                      ? <input style={{ ...S.input, ...S.inputDisabled }} value="Unlimited" disabled />
                      : <input style={S.input} type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} min="0" placeholder="0" />
                    }
                  </Field>
                </div>

                <Field label="HSN Code" hint="For tax purposes (optional)">
                  <div style={{ display: "flex", gap: "8px" }}>
                    <select style={S.select} value={hsnCode} onChange={(e) => setHsnCode(e.target.value)}>
                      <option value="">Select HSN Code</option>
                      {hsnCodesList.map((h) => (
                        <option key={h._id} value={h.hsn_code}>
                          {h.hsn_code} — {h.gst_percentage}% GST{h.description ? ` (${h.description})` : ""}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={() => setShowHSNModal(true)} style={{ ...S.btnGhost, flexShrink: 0 }}>
                      <Plus size={13} /> Add
                    </button>
                  </div>
                </Field>

                <label style={{ ...S.checkRow, cursor: "pointer" }} onClick={() => setIsGstApplicable((p) => !p)}>
                  <input
                    type="checkbox" checked={isGstApplicable}
                    onChange={(e) => setIsGstApplicable(e.target.checked)}
                    style={{ width: "16px", height: "16px", accentColor: "#1E5B3A", cursor: "pointer", flexShrink: 0, marginTop: "1px" }}
                  />
                  <div>
                    <div style={{ fontSize: "13.5px", fontWeight: "600", color: "#241E14" }}>Apply GST to this product</div>
                    <div style={{ fontSize: "12px", color: "#8A7E6A" }}>Tax will be added at checkout based on the HSN code above</div>
                  </div>
                </label>
              </Section>

              {/* ═══ 3: Category ════════════════════════════════════════════ */}
              <Section
                icon={<span style={{ fontSize: "16px" }}>🗂️</span>}
                title="Category and Description"
                subtitle="Add the right category and clear details to help customers discover your product"
                defaultOpen={true}
              >
                <Field label="Select Category" required error={subCategoryError}>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                    <select style={S.select} onChange={(e) => { if (e.target.value) { handleCategorySelect(e.target.value); setSubCategoryError(""); } }} value="">
                      <option value="">Choose a category...</option>
                      {categoryData.store.some((c) => !isCategorySelected(c)) && (
                        <optgroup label={t("form.myStoreCategories")}>
                          {categoryData.store.filter((c) => !isCategorySelected(c)).map((c) => (
                            <option key={`s-${c}`} value={c}>{c}</option>
                          ))}
                        </optgroup>
                      )}
                      {categoryData.marketplace.some((c) => !isCategorySelected(c)) && (
                        <optgroup label={t("form.marketplaceCategories")}>
                          {categoryData.marketplace.filter((c) => !isCategorySelected(c)).map((c) => (
                            <option key={`m-${c}`} value={c}>{c}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    <button type="button" onClick={() => setShowAddCategoryForm(true)} style={{ ...S.btnGhost, flexShrink: 0 }}>
                      <Plus size={13} /> New
                    </button>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap" }}>
                    {selectedCategories.map((cat, i) => (
                      <span key={i} style={S.badge("green")}>
                        {cat}
                        <button type="button" onClick={() => handleRemoveCategory(cat)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: "#1E5B3A" }}><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                </Field>

                {showAddCategoryForm && (
                  <div style={{ background: "#f9fafc", borderRadius: "10px", border: "1px solid #eef0f5", padding: "14px", marginBottom: "14px" }}>
                    <p style={{ fontSize: "13px", fontWeight: "700", color: "#241E14", margin: "0 0 4px" }}>Add New Category</p>
                    <p style={{ fontSize: "12px", color: "#6b7280", margin: "0 0 10px" }}>Check the marketplace categories first. Only categories you create count towards your limit of 10. A new category shows on your store right away, and on the MyTamilKart marketplace once it has a product and our team approves it.</p>
                    <Field label="Category Name">
                      <input style={S.input} placeholder="e.g. Gifts" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
                    </Field>
                    <Field label="First Subcategory">
                      <input style={S.input} placeholder="e.g. Birthday Gifts" value={newSubCategory} onChange={(e) => setNewSubCategory(e.target.value)} />
                    </Field>
                    {addCatError && <div style={S.errorText}><AlertCircle size={11} />{addCatError}</div>}
                    <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                      <button type="button" style={S.btnPrimary} onClick={handleCreateCategory} disabled={isCreatingCategory}>{isCreatingCategory ? "Saving..." : "Save"}</button>
                      <button type="button" style={S.btnGhost} onClick={() => { setShowAddCategoryForm(false); setAddCatError(""); }}>Cancel</button>
                    </div>
                  </div>
                )}

                {selectedCategories.map((cat, ci) => (
                  <div key={ci} style={{ marginBottom: "12px" }}>
                    <label style={{ ...S.label, color: "#1E5B3A", marginBottom: "6px" }}>Subcategory in "{cat}"</label>
                    <div style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                      <select style={S.select} onChange={(e) => { const s = subCategoryData[cat]?.find((sc) => sc.id === e.target.value); if (s) { handleSubCategorySelect({ ...s, parentCategory: cat }); setSubCategoryError(""); } }} value="">
                        <option value="">Choose subcategory...</option>
                        {subCategoryData[cat]?.map((sc, j) => <option key={j} value={sc.id}>{sc.subcategory}{sc.isMarketplace ? "" : " (my store)"}</option>)}
                      </select>
                      <button type="button" onClick={() => { setShowAddSubcatFor(cat); setNewSubcategoryName(""); setAddSubcatError(""); }} style={{ ...S.btnGhost, flexShrink: 0 }}>
                        <Plus size={13} /> Add
                      </button>
                    </div>
                    {showAddSubcatFor === cat && (
                      <div style={{ background: "#f0faf4", borderRadius: "9px", border: "1px solid #b5dfc8", padding: "12px", marginBottom: "8px" }}>
                        <input style={{ ...S.input, marginBottom: "8px" }} placeholder="Subcategory name" value={newSubcategoryName} onChange={(e) => setNewSubcategoryName(e.target.value)} />
                        {addSubcatError && <div style={S.errorText}><AlertCircle size={11} />{addSubcatError}</div>}
                        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                          <button type="button" style={{ ...S.btnPrimary, padding: "7px 16px", fontSize: "13px" }} onClick={() => handleCreateSubcategory(cat)} disabled={isCreatingSubcategory}>{isCreatingSubcategory ? "Saving..." : "Save"}</button>
                          <button type="button" style={S.btnGhost} onClick={() => setShowAddSubcatFor(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {selectedSubCategories.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", marginTop: "4px" }}>
                    {selectedSubCategories.map((sc) => (
                      <span key={sc.id || sc._id} style={S.badge("blue")}>
                        {sc.subcategory}
                        <button type="button" onClick={() => handleRemoveSubCategory(sc.id || sc._id)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: "#1a56db" }}><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}

                <Field label="Description" hint="Tell customers what makes this product special.">
                  <textarea
                    style={{ ...S.textarea, minHeight: "90px" }}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your product..."
                  />
                  <AIDescriptionGenerator
                    productName={title}
                    categories={selectedCategories}
                    subcategories={selectedSubCategories}
                    imageFiles={images}
                    imageUrls={uploadedImageUrls}
                    onDescriptionGenerated={setDescription}
                  />
                </Field>
              </Section>

              {/* ═══ 4: Photos & Videos ════════════════════════════════════ */}
              <Section
                icon={<span style={{ fontSize: "16px" }}>🖼️</span>}
                title={productType === "digital" ? "Cover Image & E-book File" : "Photos & Videos"}
                subtitle={productType === "digital" ? "Upload a cover image and your digital file" : `Add up to 5 photos/videos (${totalMedia}/5 added)`}
                defaultOpen={true}
              >
                {productType === "physical" ? (
                  <>
                    <div style={S.infoBox}>
                      <CheckCircle size={14} style={{ flexShrink: 0, marginTop: "1px" }} />
                      <span>First photo will be the main product image. You can add up to 5 photos and videos combined.</span>
                    </div>

                    <div style={S.row2}>
                      <label style={S.uploadArea}>
                        <input type="file" multiple accept="image/*" onChange={handleImageChange} style={{ display: "none" }} />
                        <span style={{ fontSize: "28px", display: "block", marginBottom: "6px" }}>📷</span>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "#5a6478" }}>Add Photos</div>
                        <div style={{ fontSize: "12px", color: "#8A7E6A", marginTop: "3px" }}>JPG, PNG, WEBP</div>
                      </label>
                      <label style={S.uploadArea}>
                        <input type="file" multiple accept="video/mp4,video/mov,video/webm,video/quicktime" onChange={handleVideoChange} style={{ display: "none" }} />
                        <span style={{ fontSize: "28px", display: "block", marginBottom: "6px" }}>🎬</span>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "#5a6478" }}>Add Videos</div>
                        <div style={{ fontSize: "12px", color: "#8A7E6A", marginTop: "3px" }}>MP4, MOV · Max 100MB</div>
                      </label>
                    </div>

                    {(uploadedImageUrls.length > 0 || images.length > 0) && (
                      <div style={{ marginBottom: "10px" }}>
                        {[
                          ...uploadedImageUrls.map((u, i) => ({ src: u, i, type: "up" })),
                          ...images.map((img, i) => ({ src: URL.createObjectURL(img), i, type: "new" })),
                        ].map((item, key) => (
                          <div key={key} style={S.thumb}>
                            <img src={item.src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            {key === 0 && <div style={S.thumbMain}>MAIN</div>}
                            <button type="button" style={S.thumbRemove}
                              onClick={() => item.type === "up"
                                ? setUploadedImageUrls((p) => p.filter((_, j) => j !== item.i))
                                : setImages((p) => p.filter((_, j) => j !== item.i))
                              }>
                              <X size={10} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {(uploadedVideoUrls.length > 0 || videos.length > 0) && (
                      <div style={{ marginBottom: "10px" }}>
                        {[
                          ...uploadedVideoUrls.map((u, i) => ({ src: u, i, type: "up" })),
                          ...videos.map((v, i) => ({ src: URL.createObjectURL(v), i, type: "new" })),
                        ].map((item, key) => (
                          <div key={key} style={{ ...S.thumb, width: "140px", height: "90px" }}>
                            <video src={item.src} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            {videoUploadProgress[item.i] !== undefined && item.type === "new" && (
                              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "3px", background: "rgba(255,255,255,0.3)" }}>
                                <div style={{ height: "100%", width: `${videoUploadProgress[item.i]}%`, background: "#1E5B3A" }} />
                              </div>
                            )}
                            <button type="button" style={{ ...S.thumbRemove, background: "rgba(211,47,47,0.8)" }}
                              onClick={() => item.type === "up"
                                ? setUploadedVideoUrls((p) => p.filter((_, j) => j !== item.i))
                                : setVideos((p) => p.filter((_, j) => j !== item.i))
                              }>
                              <X size={10} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <label style={S.uploadArea}>
                      <input type="file" accept="image/*" onChange={handleImageChange} style={{ display: "none" }} />
                      <span style={{ fontSize: "28px", display: "block", marginBottom: "6px" }}>🖼️</span>
                      <div style={{ fontSize: "13px", fontWeight: "600", color: "#5a6478" }}>Upload Cover Image</div>
                      <div style={{ fontSize: "12px", color: "#8A7E6A", marginTop: "3px" }}>This will be shown in product listings</div>
                    </label>
                    <div style={{ marginBottom: "12px" }}>
                      {[
                        ...uploadedImageUrls.map((u, i) => ({ src: u, i, type: "up" })),
                        ...images.map((img, i) => ({ src: URL.createObjectURL(img), i, type: "new" })),
                      ].map((item, key) => (
                        <div key={key} style={{ ...S.thumb, width: "110px", height: "145px" }}>
                          <img src={item.src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <button type="button" style={S.thumbRemove}
                            onClick={() => item.type === "up"
                              ? setUploadedImageUrls((p) => p.filter((_, j) => j !== item.i))
                              : setImages((p) => p.filter((_, j) => j !== item.i))
                            }>
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <EbookFileUpload
                      businessId={localStorage.getItem("business_id") || ""}
                      onUploadComplete={setDigitalFileData}
                      existingFile={digitalFileData}
                    />
                    <DigitalProductFields
                      formData={digitalSettings}
                      onChange={(field, val) => setDigitalSettings((p) => ({ ...p, [field]: val }))}
                    />
                  </>
                )}

                {mediaError && <div style={S.errorText}><AlertCircle size={11} />{mediaError}</div>}
              </Section>

              {/* ═══ OPTIONAL SECTIONS ══════════════════════════════════════ */}
              <p style={{ fontSize: "12px", fontWeight: "700", color: "#8A7E6A", letterSpacing: "0.07em", textTransform: "uppercase", margin: "6px 0 10px" }}>
                Optional Details — Enable what you need
              </p>

              {/* ─── Identity ─────────────────────────────────────────────── */}
              <div style={S.card}>
                <div style={S.toggleWrap} onClick={() => setIdentityEnabled((p) => !p)}>
                  <div style={S.toggleLeft}>
                    <div style={S.cardIconWrap("blue")}><Tag size={16} style={{ color: "#1a56db" }} /></div>
                    <div>
                      <p style={S.toggleTitle}>SKU & Brand Info</p>
                      <p style={S.toggleDesc}>Add SKU, barcode, brand name — useful for inventory tracking</p>
                    </div>
                  </div>
                  <Toggle on={identityEnabled} onChange={setIdentityEnabled} />
                </div>
                {identityEnabled && (
                  <div style={S.collapsibleBody}>
                    <div style={{ height: "14px" }} />
                    <div style={S.row2}>
                      <Field label="SKU Code" hint="Auto-generated if left empty">
                        <input style={S.input} value={sku} onChange={(e) => setSku(e.target.value)} placeholder="e.g. MUG-RD-S" />
                      </Field>
                      <Field label="Barcode / UPC / EAN">
                        <input style={S.input} value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder="e.g. 1234567890123" />
                      </Field>
                      <Field label="Brand Name">
                        <input style={S.input} value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. KS Gift Shop" />
                      </Field>
                      <Field label="Manufacturer">
                        <input style={S.input} value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} placeholder="e.g. KS Industries" />
                      </Field>
                    </div>
                  </div>
                )}
              </div>

              {/* ─── Variants ─────────────────────────────────────────────── */}
              <div style={S.card}>
                <div style={S.toggleWrap} onClick={() => setVariantsEnabled((p) => !p)}>
                  <div style={S.toggleLeft}>
                    <div style={S.cardIconWrap("amber")}><Layers size={16} style={{ color: "#c05500" }} /></div>
                    <div>
                      <p style={S.toggleTitle}>Sizes, Colors & Variants</p>
                      <p style={S.toggleDesc}>Does this product come in different sizes, colors or styles?</p>
                    </div>
                  </div>
                  <Toggle on={variantsEnabled} onChange={setVariantsEnabled} />
                </div>
                {variantsEnabled && (
                  <div style={S.collapsibleBody}>
                    <div style={{ height: "14px" }} />
                    {variants.map((v, vi) => (
                      <div key={vi} style={{ background: "#f9fafc", borderRadius: "10px", border: "1px solid #eef0f5", padding: "12px 14px", marginBottom: "10px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                          <span style={{ fontSize: "13px", fontWeight: "700", color: "#241E14" }}>{v.type}</span>
                          <button type="button" style={S.btnDanger} onClick={() => {
                            // Remove variant type and clean up associated variant images
                            const removedVariant = variants[vi];
                            if (removedVariant.type.toLowerCase() === 'color') {
                              // Remove all variant images for colors in this variant
                              setVariantImages(prev => prev.filter(img => !removedVariant.values.includes(img.color)));
                            }
                            setVariants(variants.filter((_, i) => i !== vi));
                          }}>Remove</button>
                        </div>
                        <div>
                          {v.values.map((val, vali) => (
                            <span key={vali} style={S.chip}>
                              {val}
                              <button type="button" onClick={() => {
                                // Remove variant value and clean up associated variant image if it's a color
                                const removedValue = v.values[vali];
                                if (v.type.toLowerCase() === 'color') {
                                  setVariantImages(prev => prev.filter(img => img.color !== removedValue));
                                }
                                setVariants(variants.map((vr, i) => i === vi ? { ...vr, values: vr.values.filter((_, j) => j !== vali) } : vr));
                              }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: "#1E5B3A" }}><X size={9} /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div style={{ background: "#f9fafc", borderRadius: "10px", border: "1px dashed #c8d3e8", padding: "12px 14px" }}>
                      <p style={{ fontSize: "12.5px", fontWeight: "600", color: "#4a5168", marginBottom: "10px" }}>Add a variant type</p>
                      <div style={S.row2}>
                        <Field label="Type" hint="e.g. Size, Color, Flavour, Dosage, Storage">
                          <select
                            style={S.select}
                            value={isCustomVariantType ? 'custom' : newVariantType}
                            onChange={(e) => {
                              if (e.target.value === 'custom') {
                                setNewVariantType('');
                                setIsCustomVariantType(true);
                              } else {
                                setNewVariantType(e.target.value);
                                setIsCustomVariantType(false);
                              }
                            }}
                          >
                            <option value="">Select...</option>
                            {["Size", "Color", "Material", "Weight", "Style"].map((t) => <option key={t} value={t}>{t}</option>)}
                            <option value="custom">+ Custom type...</option>
                          </select>
                          {isCustomVariantType && (
                            <input
                              style={{ ...S.input, marginTop: '8px' }}
                              value={newVariantType}
                              onChange={(e) => setNewVariantType(e.target.value)}
                              placeholder="e.g. Flavour, Dosage, Storage, Pack Size"
                              autoFocus
                            />
                          )}
                        </Field>
                        <Field label="Values" hint="Separate with commas">
                          <input style={S.input} value={newVariantValues} onChange={(e) => setNewVariantValues(e.target.value)} placeholder="e.g. S, M, L  or  Red, Blue" />
                        </Field>
                      </div>
                      <button type="button" style={{ ...S.btnOutline, marginTop: "4px" }} onClick={handleAddVariant}>
                        <Plus size={13} /> Add Variant
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* ─── Color Images (Variant Images) ────────────────────────── */}
              {variantsEnabled && variants.some(v => v.type.toLowerCase() === 'color') && (
                <div style={S.card}>
                  <div style={S.toggleWrap}>
                    <div style={S.toggleLeft}>
                      <div style={S.cardIconWrap("purple")}><img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238b5cf6' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpolyline points='21 15 16 10 5 21'/%3E%3C/svg%3E" alt="" style={{ width: 16, height: 16 }} /></div>
                      <div>
                        <p style={S.toggleTitle}>Color-Specific Images</p>
                        <p style={S.toggleDesc}>Upload images for each color variant — customers will see the image when they select a color</p>
                      </div>
                    </div>
                  </div>
                  <div style={S.collapsibleBody}>
                    <div style={{ height: "14px" }} />
                    {(() => {
                      const colorVariant = variants.find(v => v.type.toLowerCase() === 'color');
                      if (!colorVariant || !colorVariant.values.length) {
                        return <p style={{ fontSize: "13px", color: "#8A7E6A", fontStyle: "italic" }}>Add color values to the Color variant above first</p>;
                      }

                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          {colorVariant.values.map((color, idx) => {
                            const existingImage = variantImages.find(vi => vi.color === color);
                            const isUploading = uploadingVariantImage === color;

                            return (
                              <div key={idx} style={{ background: "#f9fafc", borderRadius: "10px", border: "1px solid #eef0f5", padding: "12px 14px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                  <div style={{ flex: "0 0 80px", height: "80px", borderRadius: "8px", border: "2px solid #e5e7eb", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                                    {existingImage?.image_url ? (
                                      <img src={existingImage.image_url} alt={color} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : (
                                      <span style={{ fontSize: "11px", color: "#9ca3af", textAlign: "center" }}>No image</span>
                                    )}
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: "13.5px", fontWeight: "700", color: "#241E14", marginBottom: "6px" }}>{color}</div>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      id={`color-image-${idx}`}
                                      style={{ display: "none" }}
                                      onChange={(e) => handleVariantImageUpload(color, e.target.files[0])}
                                      disabled={isUploading}
                                    />
                                    <div style={{ display: "flex", gap: "8px" }}>
                                      <label
                                        htmlFor={`color-image-${idx}`}
                                        style={{
                                          ...S.btnOutline,
                                          fontSize: "12px",
                                          padding: "6px 12px",
                                          cursor: isUploading ? "not-allowed" : "pointer",
                                          opacity: isUploading ? 0.6 : 1,
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "4px"
                                        }}
                                      >
                                        {isUploading ? "Uploading..." : existingImage ? "Change Image" : "Upload Image"}
                                      </label>
                                      {existingImage && !isUploading && (
                                        <button
                                          type="button"
                                          style={{ ...S.btnDanger, fontSize: "12px", padding: "6px 12px" }}
                                          onClick={() => handleRemoveVariantImage(color)}
                                        >
                                          Remove
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* ─── Shipping (ProductShippingDetails.js) ──────────────────── */}
              {productType !== "digital" && (
                <ProductShippingDetails
                  variants={variantsEnabled ? variants : []}
                  weightType={effectiveWeightType}
                  setWeightType={setWeightType}
                  perCombination={perCombination}
                  setPerCombination={setPerCombination}
                  rows={weightRowsState}
                  setRows={(update) => { setWeightRowsState(update); setMissingWeightKey(null); }}
                  freeShipping={freeShipping}
                  setFreeShipping={setFreeShipping}
                  chargeType={shippingChargeType}
                  setChargeType={setShippingChargeType}
                  fixedCharge={shippingFixedCharge}
                  setFixedCharge={setShippingFixedCharge}
                  ratePerKg={shippingRatePerKg}
                  setRatePerKg={setShippingRatePerKg}
                  codAvailable={codAvailable}
                  setCodAvailable={setCodAvailable}
                  shipping={shipping}
                  missingKey={missingWeightKey}
                />
              )}

              {/* ─── SEO ──────────────────────────────────────────────────── */}
              <div style={S.card}>
                <div style={S.toggleWrap} onClick={() => setSeoEnabled((p) => !p)}>
                  <div style={S.toggleLeft}>
                    <div style={S.cardIconWrap("blue")}><Search size={16} style={{ color: "#1a56db" }} /></div>
                    <div>
                      <p style={S.toggleTitle}>SEO & Google Search</p>
                      <p style={S.toggleDesc}>Help people find your product on Google (optional but recommended)</p>
                    </div>
                  </div>
                  <Toggle on={seoEnabled} onChange={setSeoEnabled} />
                </div>
                {seoEnabled && (
                  <div style={S.collapsibleBody}>
                    <div style={{ height: "14px" }} />

                    <div style={S.row2}>
                      <Field label="Meta Title" hint={`${seoMetaTitle.length}/60 characters — shown as the page title in Google`}>
                        <input
                          style={{ ...S.input, borderColor: seoMetaTitle.length > 60 ? "#d32f2f" : "#e3e7f0" }}
                          value={seoMetaTitle}
                          onChange={(e) => {
                            setSeoMetaTitle(e.target.value);
                            setIsMetaTitleManuallyEdited(true);
                          }}
                          placeholder="e.g. Best Silk Saree for Wedding"
                        />
                      </Field>
                      <Field label="Tags" hint="AI-generated or press Enter / comma to add manually">
                        <div style={{ display: "flex", gap: "6px" }}>
                          <input
                            style={S.input} value={seoTagInput}
                            onChange={(e) => setSeoTagInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); handleAddTag(); } }}
                            placeholder="e.g. saree, silk, wedding..."
                          />
                          <button type="button" onClick={handleAddTag} style={{ ...S.btnGhost, flexShrink: 0 }}><Plus size={13} /></button>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", marginTop: "6px" }}>
                          {seoTags.map((tag) => (
                            <span key={tag} style={S.badge("blue")}>
                              #{tag}
                              <button type="button" onClick={() => setSeoTags(seoTags.filter((t) => t !== tag))} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: "#1a56db" }}><X size={10} /></button>
                            </span>
                          ))}
                        </div>
                      </Field>
                    </div>

                    <Field
                      label="URL Slug"
                      hint={`Product URL: ${storeDomain}/${seoSlug || previewSlug || 'your-product-name'}/[product-id]`}
                    >
                      <div style={{ position: "relative" }}>
                        <div style={{
                          position: "absolute", left: "10px", top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "12px", color: "#8A7E6A", pointerEvents: "none"
                        }}>
                          /
                        </div>
                        <input
                          style={{ ...S.input, paddingLeft: "20px" }}
                          value={seoSlug}
                          onChange={(e) => {
                            setSeoSlug(sanitizeSlug(e.target.value));
                            setIsSlugManuallyEdited(true);
                          }}
                          placeholder="your-product-name"
                        />
                      </div>
                      <div style={{ fontSize: "11px", color: "#8A7E6A", marginTop: "4px" }}>
                        Auto-generated from product name. You can customise it.
                        Final URL will be: <strong>/{seoSlug || previewSlug || 'your-product-name'}/[product-id]</strong>
                      </div>
                    </Field>

                    {/* Meta Description — with AI Generate button */}
                    <div style={S.fieldWrap}>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "6px"
                      }}>
                        <label style={S.label}>
                          Meta Description
                        </label>
                        <button
                          type="button"
                          onClick={handleGenerateSEO}
                          disabled={seoGenerating || !title.trim()}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "5px 12px",
                            background: seoGenerating ? "#f0f2f7" : "#f0faf4",
                            border: "1px solid #b5dfc8",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#1E5B3A",
                            cursor: (seoGenerating || !title.trim()) ? "not-allowed" : "pointer",
                            opacity: (seoGenerating || !title.trim()) ? 0.6 : 1,
                            transition: "all 0.15s",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {seoGenerating ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                style={{ width: "10px", height: "10px", borderWidth: "1.5px" }}
                              />
                              Generating...
                            </>
                          ) : (
                            <>
                              <span>✨</span>
                              Generate with AI
                            </>
                          )}
                        </button>
                      </div>

                      <textarea
                        style={{
                          ...S.textarea,
                          minHeight: "70px",
                          borderColor: seoMetaDescription.length > 160 ? "#d32f2f" : "#e3e7f0"
                        }}
                        value={seoMetaDescription}
                        onChange={(e) => setSeoMetaDescription(e.target.value)}
                        placeholder="e.g. Buy premium silk saree with fast delivery across India. Best price guaranteed."
                      />

                      <p style={S.hint}>
                        {seoMetaDescription.length}/160 characters
                        {seoMetaDescription.length > 160 && (
                          <span style={{ color: "#d32f2f", marginLeft: "6px" }}>
                            Too long — trim to 160 characters
                          </span>
                        )}
                        {seoMetaDescription.length === 0 && (
                          <span style={{ color: "#8A7E6A", marginLeft: "6px" }}>
                            — shown below your product title in Google search results
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Google preview with slug URL */}
                    {seoMetaTitle && (
                      <div style={S.serpCard}>
                        <p style={{ fontSize: "11px", fontWeight: "600", color: "#8A7E6A", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 8px" }}>Google Preview</p>
                        {seoSlug && (
                          <p style={{ fontSize: "12px", color: "#1a6b3c", margin: "0 0 3px" }}>
                            {storeDomain}/{seoSlug}/[product-id]
                          </p>
                        )}
                        <p style={{ fontSize: "16px", color: "#1558d6", fontWeight: "500", margin: "0 0 3px", lineHeight: "1.3" }}>{seoMetaTitle}</p>
                        <p style={{ fontSize: "12.5px", color: "#4b5563", margin: "0", lineHeight: "1.5" }}>{seoMetaDescription || "Product description will appear here..."}</p>
                        {seoTags.length > 0 && (
                          <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {seoTags.map((tag) => <span key={tag} style={{ fontSize: "11px", color: "#8A7E6A", background: "#f0f2f7", padding: "2px 7px", borderRadius: "10px" }}>#{tag}</span>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ═══ Error message ══════════════════════════════════════════ */}
              {errorMessage && (
                <div style={{ padding: "12px 16px", background: "#fff5f5", borderRadius: "9px", border: "1px solid #ffd0d0", display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                  <AlertCircle size={15} style={{ color: "#d32f2f", flexShrink: 0 }} />
                  <span style={{ fontSize: "13.5px", color: "#c62828" }}>{errorMessage}</span>
                </div>
              )}

              {/* ═══ Form Footer ════════════════════════════════════════════ */}
              {/* FIX: submitBtn is a plain object in styles, opacity/cursor handled inline */}
              <div style={S.formFooter}>
                <button type="button" onClick={() => navigate("/gift")} style={{ ...S.btnGhost, padding: "10px 22px" }}>
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isFormProcessing}
                  style={{ ...S.submitBtn, opacity: isFormProcessing ? 0.7 : 1, cursor: isFormProcessing ? "not-allowed" : "pointer" }}
                >
                  {isFormProcessing
                    ? <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span className="spinner-border spinner-border-sm" style={{ width: "14px", height: "14px", borderWidth: "2px" }} />
                      Saving...
                    </span>
                    : giftId ? "Update Product" : "Add Product"
                  }
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>

      {/* ═══ HSN Modal ══════════════════════════════════════════════════════ */}
      {showHSNModal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <div style={S.modalHead}>
              <h5 style={{ margin: 0, fontWeight: "700", fontSize: "15px" }}>Add HSN Code</h5>
              <button type="button" onClick={() => setShowHSNModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#8A7E6A" }}><X size={17} /></button>
            </div>
            <form onSubmit={handleAddHSNCode}>
              <div style={S.modalBody}>
                {[
                  { key: "hsn_code", label: "HSN Code", req: true, type: "text", ph: "e.g. 9001" },
                  { key: "gst_percentage", label: "GST %", req: true, type: "number", ph: "e.g. 18" },
                  { key: "description", label: "Description", req: false, type: "text", ph: "Optional" },
                  { key: "category", label: "Category", req: false, type: "text", ph: "Optional" },
                ].map(({ key, label, req, type, ph }) => (
                  <Field key={key} label={label} required={req} error={hsnErrors[key]}>
                    <input
                      style={{ ...S.input, borderColor: hsnErrors[key] ? "#d32f2f" : "#e3e7f0" }}
                      type={type} step={key === "gst_percentage" ? "0.01" : undefined}
                      value={newHSN[key]} onChange={(e) => setNewHSN({ ...newHSN, [key]: e.target.value })}
                      placeholder={ph}
                    />
                  </Field>
                ))}
              </div>
              <div style={S.modalFoot}>
                <button type="button" onClick={() => setShowHSNModal(false)} style={S.btnGhost}>Cancel</button>
                <button type="submit" style={S.btnPrimary}>Add Code</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ Payment Modal ══════════════════════════════════════════════════ */}
      {showPaymentModal && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, textAlign: "center" }}>
            <div style={{ padding: "28px 24px" }}>
              <div style={{ fontSize: "42px", marginBottom: "12px" }}>💳</div>
              <h3 style={{ fontWeight: "700", fontSize: "17px", margin: "0 0 8px" }}>Add Payment Details</h3>
              <p style={{ color: "#5a6478", fontSize: "14px", margin: "0 0 22px", lineHeight: "1.5" }}>
                To start selling this product, please add your payment details first.
              </p>
              <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                <button type="button" onClick={() => { setShowPaymentModal(false); navigate("/gift"); }} style={S.btnGhost}>Skip for now</button>
                <button type="button" onClick={() => { setShowPaymentModal(false); navigate("/create-payment"); }} style={S.btnPrimary}>Add Payment Details</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default AddGift;